import { algoliasearch, type SearchClient } from "algoliasearch";
import { getUnixTime, subMinutes } from "date-fns";
import { makeAutoObservable, runInAction } from "mobx";

import { apiClient } from "../../common/apiClient";
import { logger } from "../../common/logger";
import { sendSearch } from "../../common/tracking";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type { SupportedLocaleType } from "../../common/types/languageTypes";
import { type AlgoliaEvent, type AllSearchTypes, SEARCH_TYPE, type SearchData } from "../../common/types/searchTypes";
import { forceArray } from "../../common/utils";
import { keepIdOnly } from "../../common/utils/segmentUtils";
import theme from "../../themes/theme";
import authStore from "../auth/authStore";
import { filterHitsVisibleForSegment } from "../cms/segmentStore";
import featuresStore from "../features/featuresStore";
import productStore from "../product/productStore";
import { type SearchDomainWithAlgoliaClient, searchInDomain } from "./searchUtils";

const algoliaClient: SearchClient = algoliasearch(
   theme.searchConfig.algoliaConfig.appId,
   theme.searchConfig.algoliaConfig.searchApiKey
);

const searchDomains: SearchDomainWithAlgoliaClient[] = theme.searchConfig.searchDomains.map((domain) => ({
   ...domain,
   algoliaClient
}));

let queueTimeout: number | undefined;

type SearchStoreType = BaseStoreType &
   Record<SEARCH_TYPE, SearchData> & {
      query: string;
      eventQueue: AlgoliaEvent[];
      totalHits: number;

      clear(field: SEARCH_TYPE, includeQuery?: boolean): void;
      performSearches(type: SEARCH_TYPE, locale?: SupportedLocaleType): Promise<void>;
      reportAlgoliaClick(
         id: string,
         index: string,
         queryID?: string,
         position?: number | null,
         fromSearch?: boolean,
         type?: AllSearchTypes
      ): void;
      reportAlgoliaAddToCart(sku: string, index: string, queryID?: string, fromSearch?: boolean, type?: AllSearchTypes): void;
      reportDetailsViewed(sku: string, index: string, queryID?: string): void;
      reportArticleViewed(id: string, index: string, queryID?: string): void;
      reportEvent(
         eventType: string,
         eventName: string,
         id: string,
         position: number | null,
         type: AllSearchTypes | null,
         index: string,
         queryID: string
      ): void;
      queueEvent(eventsToQueue: AlgoliaEvent | AlgoliaEvent[]): void;
      sendQueue(): void;
   };

const searchStore: SearchStoreType = makeAutoObservable({
   query: "",
   eventQueue: [],
   quick: {
      results: [],
      resultsFor: ""
   },
   full: {
      results: [],
      resultsFor: ""
   },
   totalHits: 0,

   async performSearches(type, locale) {
      const query = searchStore.query;

      const company = authStore.getCurrentCompany();
      const customerGroup =
         featuresStore.searchResultsSortedByCustomerGroupSales && company ? keepIdOnly(company.customerGroup) : "default";

      const algoliaUserToken = authStore.isLoggedIn() ? authStore.getAlgoliaUserToken() : undefined;
      const results = await Promise.all(
         searchDomains.map((searchDomain) =>
            searchInDomain(
               query,
               searchDomain,
               type,
               customerGroup,
               algoliaUserToken,
               featuresStore.prioritizeAgreementAssortment,
               productStore.assortment,
               productStore.replacementProducts,
               (skus) => productStore.resolveSkus(skus, true, false),
               filterHitsVisibleForSegment,
               locale
            )
         )
      );

      if (query === searchStore.query) {
         runInAction(() => {
            searchStore[type].results = results;
            searchStore[type].resultsFor = query;
            searchStore.totalHits = results.reduce((total, result) => total + result.hits.length, 0);
            const searchType = type === "full" ? "static" : "dynamic";
            sendSearch(searchType, this.totalHits, query);
         });
      }
   },

   clear: (field: SEARCH_TYPE = SEARCH_TYPE.QuickSearch, includeQuery = false) => {
      searchStore[field].results = [];
      if (includeQuery) {
         searchStore.query = "";
      }
   },

   /**
    * @param sku {string}
    * @param position {number | null}
    * @param fromSearch {boolean}
    * @param type {"quick" | "direct"}
    */
   reportAlgoliaClick: (id, index, queryID = "", position = null, fromSearch = false, type = "direct") => {
      const eventName = fromSearch ? "Clicked From Search" : "Clicked";
      searchStore.reportEvent("click", eventName, id, position, type, index, queryID);
   },

   reportAlgoliaAddToCart: (sku, index, queryID = "", fromSearch = false, type = "direct") => {
      const eventName = fromSearch ? "Added To Cart From Search" : "Added to cart";
      searchStore.reportEvent("conversion", eventName, sku, null, type, index, queryID);
   },

   reportDetailsViewed: (sku, index, queryID = "") => {
      searchStore.reportEvent("view", "Product Viewed", sku, null, null, index, queryID);
   },

   reportArticleViewed: (id, index, queryID = "") => {
      searchStore.reportEvent("view", "Article Viewed", id, null, null, index, queryID);
   },

   reportEvent: (eventType, eventName, id, position, _type, index, queryID) => {
      if (!authStore.isLoggedIn()) {
         return;
      }

      const userToken = authStore.getAlgoliaUserToken();

      const event: AlgoliaEvent = {
         eventType,
         eventName,
         objectIDs: forceArray(id),
         index,
         queryID,
         userToken,
         timestamp: getUnixTime(subMinutes(new Date(), 5)) * 1000
      };

      if (position != null) {
         event.positions = [position];
      }

      searchStore.queueEvent(event);
   },

   queueEvent: (eventsToQueue) => {
      const eventArray = forceArray(eventsToQueue);
      for (const event of eventArray) {
         searchStore.eventQueue.push(event);
      }

      logger.log("search", `Algolia event(s) queued, current queue size: ${searchStore.eventQueue.length}`, eventsToQueue);

      if (searchStore.eventQueue.length > 25) {
         void searchStore.sendQueue();
      } else {
         if (queueTimeout != null) {
            clearTimeout(queueTimeout);
         }

         queueTimeout = window.setTimeout(searchStore.sendQueue, 30_000);
      }
   },

   sendQueue: () => {
      if (searchStore.eventQueue.length === 0) {
         return;
      }
      clearTimeout(queueTimeout);

      // Retrieve the queue contents current being processed, and empty the actual queue.
      // If the request fails we return these events to the queue.
      const queueContents = [...searchStore.eventQueue];
      searchStore.eventQueue = [];

      logger.log("search", `Sending ${queueContents.length} events to Algolia`);
      return apiClient("https://insights.algolia.io/1/events")
         .headers({
            "X-Algolia-Application-Id": theme.searchConfig.algoliaConfig.appId,
            "X-Algolia-API-Key": theme.searchConfig.algoliaConfig.searchApiKey
         })
         .post({ events: queueContents })
         .json(() => {
            logger.log("search", `${queueContents.length} events reported to Algolia`);
         })
         .catch((err) => {
            logger.warn("search", `${queueContents.length} events where rejected by Algolia error!`, err);
         });
   },

   clearCompanySpecificData: () => {
      // add methods to clear company specific data
   }
} satisfies SearchStoreType);

export default searchStore;
